import React from "react";

// Define the available typography variants
type TypographyVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "div";

// Define the props interface
interface TypographyProps {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
  title: string;
  id?: string;
}

// Create a mapping of variants to their corresponding HTML elements
const variantMapping: Record<TypographyVariant, keyof JSX.IntrinsicElements> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  p: "p",
  div: "div",
};

// Typography component
export const Typography: React.FC<TypographyProps> = ({
  variant,
  children,
  className,
  id,
  title,
  ...rest
}) => {
  const Component = variantMapping[variant] || "p";

  const isSemanticHeading = variant.startsWith("h");
  const headingLevel = isSemanticHeading
    ? parseInt(variant.slice(1))
    : undefined;
  // Determine role:
  // - If semantic heading tag: no role needed
  // - If non-heading but role heading needed (e.g., div with heading role), set role + aria-level
  // - Otherwise no role
  const role = !isSemanticHeading && variant === "div" ? "heading" : undefined;

  const ariaLevel =
    role === "heading" && headingLevel ? headingLevel : undefined;

  return (
    <Component
      className={className}
      id={id}
      title={title !== children ? title : undefined}
      role={role}
      aria-level={ariaLevel}
      {...rest}
    >
      {children}
    </Component>
  );
};
